<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import api from '@/services/api'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  /**
   * Tipo de retorno ao selecionar um componente:
   * - 'fileName': retorna apenas o nome do arquivo (sem caminho)
   * - 'fullPath': retorna o caminho relativo completo
   */
  returnType: {
    type: String,
    default: 'fileName',
    validator: (v) => ['fileName', 'fullPath'].includes(v)
  },
  /**
   * Se true, inclui a extensão do arquivo no valor retornado.
   * Ex: 'EmbarquesAdministrar.vue' ao invés de 'EmbarquesAdministrar'
   */
  includeExtension: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'selecionado'])

const loading = ref(false)
const componentes = ref([])
const filtro = ref('')

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

const componentesPorPasta = computed(() => {
  const grupos = {}
  const termo = filtro.value.toLowerCase().trim()

  for (const comp of componentes.value) {
    const pasta = comp.modulo || '(raiz)'

    // Aplica filtro por pasta, nome do componente ou caminho
    if (termo) {
      const nomeMatch = comp.nomeArquivo.toLowerCase().includes(termo)
      const pastaMatch = pasta.toLowerCase().includes(termo)
      const caminhoMatch = (comp.caminhoRelativo || '').toLowerCase().includes(termo)
      if (!nomeMatch && !pastaMatch && !caminhoMatch) continue
    }

    if (!grupos[pasta]) grupos[pasta] = []
    grupos[pasta].push(comp)
  }
  return grupos
})

const carregarComponentes = async () => {
  loading.value = true
  try {
    const response = await api.get('/Setup/componentes-vue', { _suppressLoading: true })
    componentes.value = response.data || []
  } catch (e) {
    componentes.value = []
  } finally {
    loading.value = false
  }
}

watch(() => props.modelValue, (val) => {
  if (val) {
    const { token, zIndex } = reservarZIndex()
    tokenZIndex.value = token
    zIndexReservado.value = zIndex
    carregarComponentes()
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
    filtro.value = ''
  }
})

onUnmounted(() => {
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})

const obterValorRetorno = (comp) => {
  if (props.returnType === 'fullPath') {
    return props.includeExtension ? comp.caminhoRelativo : comp.idDocumentacao
  }
  // fileName
  return props.includeExtension ? comp.nomeArquivoComExtensao : comp.nomeArquivo
}

const selecionar = (comp) => {
  const valor = obterValorRetorno(comp)
  emit('selecionado', {
    ...comp,
    valorRetorno: valor
  })
  emit('update:modelValue', false)
}

const fechar = () => {
  emit('update:modelValue', false)
}
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="modal-overlay-seletor" :style="{ zIndex: zIndexEfetivo }" @click.self="fechar">
      <div class="modal-seletor-componente">
        <div class="modal-header-seletor">
          <h5 class="mb-0"><i class="bi bi-folder me-2 text-primary"></i> Selecionar Componente Vue</h5>
          <button class="btn-close" @click="fechar"></button>
        </div>

        <div class="modal-body-seletor">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary"></div>
            <p class="mt-2 text-muted">Carregando componentes...</p>
          </div>

          <template v-else>
            <input type="text" class="form-control mb-3"
                   placeholder="Filtrar pastas ou componentes..." v-model="filtro">

            <div v-if="Object.keys(componentesPorPasta).length === 0" class="text-center py-4 text-muted">
              <i class="bi bi-search me-1"></i> Nenhum componente encontrado para "{{ filtro }}".
            </div>

            <div class="arvore-pastas">
              <div v-for="(comps, pasta) in componentesPorPasta" :key="pasta">
                <h6 class="pasta-titulo">
                  <i class="bi bi-folder2 me-1 text-warning"></i> {{ pasta || '(raiz)' }}
                </h6>
                <div v-for="comp in comps" :key="comp.caminhoRelativo"
                     :class="['componente-item', { 'ja-documentado': comp.jaDocumentado }]"
                     @click="selecionar(comp)">
                  <i :class="['bi', comp.jaDocumentado ? 'bi-file-earmark-check text-success' : 'bi-file-earmark text-muted', 'me-2']"></i>
                  <div class="componente-info">
                    <span class="componente-nome">{{ comp.nomeArquivoComExtensao }}</span>
                    <small class="componente-caminho text-muted">{{ comp.idDocumentacao }}</small>
                  </div>
                  <span v-if="comp.jaDocumentado" class="badge bg-success ms-auto">Documentado</span>
                  <span v-else class="badge bg-secondary ms-auto">Sem doc</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-overlay-seletor {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-seletor-componente {
  background: var(--bs-body-bg);
  width: 700px;
  max-width: 90vw;
  max-height: 80vh;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-header-seletor {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body-seletor {
  padding: 1rem 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.arvore-pastas {
  max-height: 400px;
  overflow-y: auto;
}

.pasta-titulo {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--bs-border-color);
  margin-top: 0.5rem;
}

.componente-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem 0.4rem 1.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
}

.componente-item:hover {
  background: var(--bs-tertiary-bg);
}

.componente-item.ja-documentado {
  opacity: 0.8;
}

.componente-info {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.componente-nome {
  font-size: 0.9rem;
  font-weight: 500;
}

.componente-caminho {
  font-size: 0.75rem;
}
</style>
